/**
 * As cinco seções numeradas do trilho de `/revisao`, na ordem de `REVISAO_RAIL`.
 * O corpo de cada uma chega pronto: esta peça não busca, não persiste e não lê store.
 */
import {
  BookOpen,
  CalendarDays,
  Layers,
  SlidersHorizontal,
  Tags,
  type LucideIcon,
} from "lucide-react";
import type { QueueOrder } from "../../../../core/store/useRevisaoSelecaoStore";
import type { DeckCount } from "../../../../sdk/@types/Revisao";
import AgendaFita from "../AgendaFita";
import DimensaoSecao from "../DimensaoSecao";
import ModoDeEstudoSecao from "../ModoDeEstudoSecao";
import RevisaoFilterSection from "../filtros/RevisaoFilterSection";
import {
  baralhosDoDiaFixado,
  idSecao,
  motivoBaralhosDoDiaFixado,
  motivoDiaFixado,
  REVISAO_RAIL,
  type AgendaDia,
  type RevisaoRailId,
} from "./revisaoHome";

const ICONE: Record<RevisaoRailId, LucideIcon> = {
  agenda: CalendarDays,
  materia: BookOpen,
  palavras: Tags,
  baralhos: Layers,
  modo: SlidersHorizontal,
};

const DESCRICAO: Record<RevisaoRailId, string> = {
  agenda: "Os próximos sete dias, com os cartões que vencem em cada um.",
  materia: "Escolha uma ou mais matérias.",
  palavras: "Escolha uma ou mais palavras-chave.",
  baralhos: "Escolha um ou mais baralhos.",
  modo: "Antecipar cartões que ainda não venceram, o limite da sessão e a ordem da fila.",
};

export interface RevisaoHomeSecoesProps {
  agenda: AgendaDia[];
  diaFixado: string | null;
  onFixarDia: (iso: string | null) => void;
  onPraticarDia: (iso: string) => void;
  materias: { id: string; label: string; count: number }[];
  materiasSelecionadas: string[];
  onToggleMateria: (id: string) => void;
  palavras: DeckCount[];
  palavrasSelecionadas: string[];
  onTogglePalavra: (value: string) => void;
  baralhos: { name: string; count: number }[];
  baralhosSelecionados: string[];
  onToggleBaralho: (name: string) => void;
  ahead: boolean;
  onAhead: (value: boolean) => void;
  limit: number;
  onLimit: (value: number) => void;
  queueOrder: QueueOrder;
  onQueueOrder: (value: QueueOrder) => void;
}

function corpo(id: "agenda" | "modo", props: RevisaoHomeSecoesProps): JSX.Element {
  switch (id) {
    case "agenda":
      return (
        <AgendaFita
          dias={props.agenda}
          diaFixado={props.diaFixado}
          onFixarDia={props.onFixarDia}
          onPraticarDia={props.onPraticarDia}
        />
      );
    case "modo":
      return (
        <ModoDeEstudoSecao
          ahead={props.ahead}
          onAhead={props.onAhead}
          limit={props.limit}
          onLimit={props.onLimit}
          queueOrder={props.queueOrder}
          onQueueOrder={props.onQueueOrder}
        />
      );
  }
}

export default function RevisaoHomeSecoes(props: RevisaoHomeSecoesProps): JSX.Element {
  // Dia fixado já vence a seleção na leitura (`entregaDaLeitura`); isto é só a
  // tela deixando de prometer o que a seleção manual não vai mais produzir
  // (design.md Decisão 5). Nenhuma opção é removida — só marcada indisponível.
  const temDiaFixado = props.diaFixado !== null;
  const baralhosFixados = baralhosDoDiaFixado(props.agenda, props.diaFixado);

  return (
    <div className="flex flex-col gap-4">
      {REVISAO_RAIL.map((entry) => {
        const Icon = ICONE[entry.id];
        const icon = <Icon aria-hidden="true" />;

        if (entry.id === "materia") {
          return (
            <DimensaoSecao
              key={entry.id}
              id={idSecao("materia")}
              step={entry.step}
              accent={entry.accent}
              icon={icon}
              title={entry.label}
              description={DESCRICAO.materia}
              options={props.materias.map((m) => ({
                value: m.id,
                label: m.count > 0 ? `${m.label} · ${m.count}` : m.label,
              }))}
              selected={props.materiasSelecionadas}
              onToggle={props.onToggleMateria}
              noun="matérias"
              searchPlaceholder="Buscar matéria…"
              emptyLabel="Nenhuma matéria disponível."
              desabilitada={temDiaFixado ? { motivo: motivoDiaFixado("matéria") } : undefined}
            />
          );
        }

        if (entry.id === "palavras") {
          return (
            <DimensaoSecao
              key={entry.id}
              id={idSecao("palavras")}
              step={entry.step}
              accent={entry.accent}
              icon={icon}
              title={entry.label}
              description={DESCRICAO.palavras}
              options={props.palavras.map((p) => ({
                value: p.name,
                label: p.count > 0 ? `${p.name} · ${p.count}` : p.name,
              }))}
              selected={props.palavrasSelecionadas}
              onToggle={props.onTogglePalavra}
              noun="palavras-chave"
              searchPlaceholder="Buscar palavra-chave…"
              emptyLabel="Nenhuma palavra-chave disponível."
              desabilitada={
                temDiaFixado ? { motivo: motivoDiaFixado("palavra-chave") } : undefined
              }
            />
          );
        }

        if (entry.id === "baralhos") {
          return (
            <DimensaoSecao
              key={entry.id}
              id={idSecao("baralhos")}
              step={entry.step}
              accent={entry.accent}
              icon={icon}
              title={entry.label}
              description={DESCRICAO.baralhos}
              options={props.baralhos.map((b) => ({
                value: b.name,
                label: b.count > 0 ? `${b.name} · ${b.count}` : b.name,
              }))}
              selected={props.baralhosSelecionados}
              onToggle={props.onToggleBaralho}
              noun="baralhos"
              searchPlaceholder="Buscar baralho…"
              emptyLabel="Nenhum baralho disponível."
              fixadas={temDiaFixado ? baralhosFixados : undefined}
              desabilitada={
                temDiaFixado ? { motivo: motivoBaralhosDoDiaFixado() } : undefined
              }
            />
          );
        }

        return (
          <RevisaoFilterSection
            key={entry.id}
            id={idSecao(entry.id)}
            step={entry.step}
            accent={entry.accent}
            title={entry.label}
            description={DESCRICAO[entry.id]}
            icon={icon}
          >
            {corpo(entry.id, props)}
          </RevisaoFilterSection>
        );
      })}
    </div>
  );
}
